import React, { useState, useEffect, useMemo } from "react";
import { 
  Home, Droplets, Sun, Zap, AlertTriangle, ShieldCheck, 
  Activity, Power, RefreshCw, Radio, Bell, ArrowRight, 
  Gauge, Clock, CheckCircle2, Sliders, BatteryCharging,
  Cpu, Thermometer, Wind, Sparkles, Copy, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export interface CriticalLoad {
  id: string;
  name: string;
  powerWatts: number;
  critical: boolean;
  active: boolean;
  category: "refrigeracion" | "comunicacion" | "iluminacion" | "ventilacion" | "climatizacion";
}

export const EcoSmartHomeOS: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"cistern" | "luma" | "hydro" | "hardware">("cistern");

  // --- 4. REAL HARDWARE & LIVE TELEMETRY STATE ---
  const [telemetryMode, setTelemetryMode] = useState<"demo" | "live-esp32">("demo");
  const [esp32Ip, setEsp32Ip] = useState<string>("192.168.1.185");
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);

  const syncLiveESP32 = async () => {
    setIsSyncing(true);
    try {
      // Intento de conexión con la IP local del ESP32 en la red doméstica
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(`http://${esp32Ip.replace(/^http:\/\//, "")}/api/telemetry`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        if (typeof data.waterLevelCm === "number") setWaterLevelCm(data.waterLevelCm);
        if (typeof data.gridVoltage === "number") {
          setGridVoltage(data.gridVoltage);
          setIsGridOnline(data.gridVoltage > 85);
        }
        if (typeof data.batterySoC === "number") setBatterySoC(data.batterySoC);
        if (typeof data.solarWatts === "number") setSolarGenerationWatts(data.solarWatts);
        setLastSyncTime(new Date().toLocaleTimeString());
        toast.success(`✓ Telemetría en vivo sincronizada desde ESP32 (${esp32Ip})`);
      } else {
        throw new Error("Respuesta inválida");
      }
    } catch {
      toast.warning(`No se pudo conectar a ${esp32Ip}. Revisa que tu ESP32 esté encendido en tu red Wi-Fi. Mostrando datos de referencia.`);
    } finally {
      setIsSyncing(false);
    }
  };

  const downloadFirmwareCode = () => {
    const code = `// =================================================================
// PROMETHEUS LAB AI - FIRMWARE IoT PARA CASA AUTOSUSTENTABLE (PR)
// Microcontrolador: ESP32-S3 / ESP32 NodeMCU
// Sensores: JSN-SR04T (Cisterna) + ZMPT101B (LUMA) + SCT-013 (Consumo)
// =================================================================
#include <WiFi.h>
#include <WebServer.h>
#include <ArduinoJson.h>

const char* ssid = "TU_WIFI_DE_CASA";
const char* password = "TU_PASSWORD";

// Pines de Hardware
#define TRIG_PIN 5      // JSN-SR04T Trigger
#define ECHO_PIN 18     // JSN-SR04T Echo
#define ZMPT_PIN 34     // ZMPT101B Sensor Voltaje Red (Analógico)
#define RELAY_PUMP 19   // Control de Bomba (vía Contactor AC)

WebServer server(80);

float readCisternCm() {
  digitalWrite(TRIG_PIN, LOW);
  delayMicroseconds(2);
  digitalWrite(TRIG_PIN, HIGH);
  delayMicroseconds(10);
  digitalWrite(TRIG_PIN, LOW);
  long duration = pulseIn(ECHO_PIN, HIGH, 30000);
  if (duration == 0) return 0;
  return (duration * 0.0343) / 2.0;
}

float readGridVoltage() {
  int raw = analogRead(ZMPT_PIN);
  // Calibración para 120V AC 60Hz estándar de Puerto Rico
  if (raw < 100) return 0.0;
  return 120.0 + ((raw - 2048) * 0.05);
}

void handleTelemetry() {
  float distanceCm = readCisternCm();
  float waterLevel = 180.0 - distanceCm; // Tanque de 180cm de altura
  if (waterLevel < 0) waterLevel = 0;
  float voltage = readGridVoltage();

  StaticJsonDocument<256> doc;
  doc["waterLevelCm"] = waterLevel;
  doc["gridVoltage"] = voltage;
  doc["pumpActive"] = digitalRead(RELAY_PUMP) == HIGH;
  doc["timestamp"] = millis();

  String response;
  serializeJson(doc, response);
  server.sendHeader("Access-Control-Allow-Origin", "*");
  server.send(200, "application/json", response);
}

void setup() {
  Serial.begin(115200);
  pinMode(TRIG_PIN, OUTPUT);
  pinMode(ECHO_PIN, INPUT);
  pinMode(RELAY_PUMP, OUTPUT);
  digitalWrite(RELAY_PUMP, LOW);

  WiFi.begin(ssid, password);
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
    Serial.print(".");
  }
  Serial.println("");
  Serial.print("ESP32 Online! IP: ");
  Serial.println(WiFi.localIP());

  server.on("/api/telemetry", handleTelemetry);
  server.begin();
}

void loop() {
  server.handleClient();
}
`;
    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Prometheus_EcoSmartHome_ESP32.ino";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("✓ Archivo 'Prometheus_EcoSmartHome_ESP32.ino' descargado. Ábrelo en Arduino IDE.");
  };

  // --- 1. CISTERNA INTELIGENTE STATE ---
  const [cisternHeightCm, setCisternHeightCm] = useState<number>(180);
  const [tankRadiusCm, setTankRadiusCm] = useState<number>(60); // Cilindro
  const [waterLevelCm, setWaterLevelCm] = useState<number>(135);
  const [pumpActive, setPumpActive] = useState<boolean>(false);
  const [dryRunProtection, setDryRunProtection] = useState<boolean>(true);
  const [aaaServiceActive, setAaaServiceActive] = useState<boolean>(true);
  const [alertTelegramEnabled, setAlertTelegramEnabled] = useState<boolean>(true);
  const [autoRefillSolar, setAutoRefillSolar] = useState<boolean>(true);

  // Cálculos de Cisterna
  const waterPercentage = useMemo(() => {
    return Math.min(100, Math.max(0, Math.round((waterLevelCm / cisternHeightCm) * 100)));
  }, [waterLevelCm, cisternHeightCm]);

  const totalGallons = useMemo(() => {
    // V = pi * r^2 * h (cm^3) / 3785.41
    const volLiters = (Math.PI * Math.pow(tankRadiusCm, 2) * cisternHeightCm) / 1000;
    return Math.round(volLiters * 0.264172);
  }, [tankRadiusCm, cisternHeightCm]);

  const currentGallons = useMemo(() => {
    const volLiters = (Math.PI * Math.pow(tankRadiusCm, 2) * waterLevelCm) / 1000;
    return Math.round(volLiters * 0.264172);
  }, [tankRadiusCm, waterLevelCm]);

  // Simulación de consumo de bomba
  useEffect(() => {
    let interval: any;
    if (pumpActive) {
      interval = setInterval(() => {
        setWaterLevelCm((prev) => {
          if (prev <= 20 && dryRunProtection) {
            setPumpActive(false);
            toast.error("⚠️ Protección contra Marcha en Seco ACTIVADA: Bomba apagada para evitar que se queme.");
            return prev;
          }
          return Math.max(0, prev - 0.5);
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [pumpActive, dryRunProtection]);

  // --- 2. GESTOR DE APAGONES LUMA STATE ---
  const [gridVoltage, setGridVoltage] = useState<number>(120);
  const [isGridOnline, setIsGridOnline] = useState<boolean>(true);
  const [batteryCapacityWh, setBatteryCapacityWh] = useState<number>(2560); // 12.8V 200Ah / 24V 100Ah
  const [batterySoC, setBatterySoC] = useState<number>(92); // %
  const [solarGenerationWatts, setSolarGenerationWatts] = useState<number>(450);

  const [criticalLoads, setCriticalLoads] = useState<CriticalLoad[]>([
    { id: "fridge", name: "Nevera Inverter (Alimentos & Medicinas)", powerWatts: 120, critical: true, active: true, category: "refrigeracion" },
    { id: "router", name: "Router Fibra Óptica & Teléfono", powerWatts: 18, critical: true, active: true, category: "comunicacion" },
    { id: "lights", name: "Luces LED Casa (Cocina, Sala, Baño)", powerWatts: 45, critical: true, active: true, category: "iluminacion" },
    { id: "fans", name: "Abanicos de Techo / DC Portátiles", powerWatts: 65, critical: true, active: true, category: "ventilacion" },
    { id: "ac", name: "Aire Acondicionado 12k BTU Inverter", powerWatts: 950, critical: false, active: false, category: "climatizacion" },
  ]);

  const totalActiveWatts = useMemo(() => {
    return criticalLoads.filter((l) => l.active).reduce((sum, l) => sum + l.powerWatts, 0);
  }, [criticalLoads]);

  const netPowerWatts = useMemo(() => {
    if (isGridOnline) return 0;
    return solarGenerationWatts - totalActiveWatts;
  }, [isGridOnline, solarGenerationWatts, totalActiveWatts]);

  const remainingHours = useMemo(() => {
    if (isGridOnline) return 999;
    const availableWh = (batteryCapacityWh * (batterySoC / 100));
    if (netPowerWatts >= 0) return 999; // Paneles solares cubren el 100%
    const dischargeRate = Math.abs(netPowerWatts);
    return dischargeRate > 0 ? (availableWh / dischargeRate).toFixed(1) : "999";
  }, [isGridOnline, batteryCapacityWh, batterySoC, netPowerWatts]);

  const toggleLoad = (id: string) => {
    setCriticalLoads((prev) =>
      prev.map((load) => (load.id === id ? { ...load, active: !load.active } : load))
    );
  };

  const toggleGrid = () => {
    const nextState = !isGridOnline;
    setIsGridOnline(nextState);
    if (!nextState) {
      setGridVoltage(0);
      toast.warning("🚨 Apagón Detectado: Conmutando automáticamente a Sistema Solar Aislado (Islanding Mode).");
      // Desactivar cargas no críticas automáticamente
      setCriticalLoads((prev) =>
        prev.map((l) => (!l.critical ? { ...l, active: false } : l))
      );
    } else {
      setGridVoltage(120);
      toast.success("✓ Servicio Eléctrico de Red Restablecido (120V 60Hz). Baterías en modo de carga.");
    }
  };

  // --- 3. HUERTO HIDROPÓNICO VERTICAL STATE ---
  const [moistureLevel, setMoistureLevel] = useState<number>(68);
  const [phLevel, setPhLevel] = useState<number>(6.2);
  const [ecLevel, setEcLevel] = useState<number>(1.8); // mS/cm
  const [hydroPumpRunning, setHydroPumpRunning] = useState<boolean>(true);
  const [pumpOnMinutes, setPumpOnMinutes] = useState<number>(15);
  const [pumpOffMinutes, setPumpOffMinutes] = useState<number>(45);

  return (
    <div className="bg-card border border-border rounded-2xl p-4 md:p-6 space-y-6 shadow-xs relative overflow-hidden">
      {/* Header & Status Indicator */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono-tech uppercase font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Eco-Smart Home OS · Puerto Rico
            </span>
            <span className="text-xs text-muted-foreground font-mono-tech font-bold">v2.4 IoT Resiliente</span>
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl font-bold tracking-tight text-foreground mt-1">
            Centro de Domótica & <span className="text-primary italic font-normal">Autosustentabilidad</span>
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">
            Monitoreo en tiempo real de cisterna, protección ante apagones de LUMA y automatización de cultivos.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-1.5 bg-muted/40 border border-border p-1 rounded-xl font-mono-tech text-xs flex-wrap">
          <button
            onClick={() => setActiveTab("cistern")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer font-bold ${
              activeTab === "cistern"
                ? "bg-card text-sky-500 border border-border shadow-xs font-extrabold"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
            }`}
          >
            <Droplets className="w-3.5 h-3.5" />
            <span>1. Cisterna & Agua</span>
          </button>

          <button
            onClick={() => setActiveTab("luma")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer font-bold ${
              activeTab === "luma"
                ? "bg-card text-amber-500 border border-border shadow-xs font-extrabold"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
            }`}
          >
            <Zap className="w-3.5 h-3.5" />
            <span>2. Gestor Apagones</span>
          </button>

          <button
            onClick={() => setActiveTab("hydro")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer font-bold ${
              activeTab === "hydro"
                ? "bg-card text-emerald-500 border border-border shadow-xs font-extrabold"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
            }`}
          >
            <Sun className="w-3.5 h-3.5" />
            <span>3. Huerto Solar</span>
          </button>

          <button
            onClick={() => setActiveTab("hardware")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer font-bold ${
              activeTab === "hardware"
                ? "bg-card text-purple-500 border border-border shadow-xs font-extrabold"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
            }`}
          >
            <Cpu className="w-3.5 h-3.5 text-purple-400" />
            <span>4. Hardware Real & ESP32</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 1. CISTERNA INTELIGENTE & PRESIÓN SOLAR (ESP32 IoT) */}
      {/* ========================================================================= */}
      {activeTab === "cistern" && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Visual Tank Graphic */}
            <div className="lg:col-span-4 bg-muted/30 border border-border rounded-2xl p-5 flex flex-col items-center justify-between relative shadow-xs">
              <div className="w-full flex items-center justify-between text-xs font-mono-tech text-muted-foreground mb-2">
                <span>SENSOR JSN-SR04T</span>
                <span className="text-sky-500 font-bold">{waterPercentage}% LLENO</span>
              </div>

              {/* Cylindrical Tank Graphic */}
              <div className="relative w-44 h-64 border-2 border-sky-500/40 rounded-3xl overflow-hidden bg-sky-950/20 shadow-inner flex flex-col justify-end">
                {/* Water Level Fill */}
                <div 
                  className="w-full bg-gradient-to-t from-sky-600 via-sky-500 to-cyan-400 transition-all duration-500 relative flex items-start justify-center pt-2"
                  style={{ height: `${waterPercentage}%` }}
                >
                  {/* Wave Animation */}
                  <div className="absolute top-0 inset-x-0 h-2 bg-cyan-200/50 blur-[1px] animate-pulse" />
                  <span className="text-xs font-mono-tech font-bold text-white drop-shadow">
                    {currentGallons} gal
                  </span>
                </div>

                {/* Dry Run Limit Line */}
                <div 
                  className="absolute inset-x-0 border-b-2 border-dashed border-red-500/80 pointer-events-none"
                  style={{ bottom: "15%" }}
                >
                  <span className="absolute right-2 -top-4 text-[9px] font-mono-tech text-red-400 font-bold bg-black/60 px-1 rounded">
                    Límite Seco (15%)
                  </span>
                </div>
              </div>

              <div className="mt-4 text-center font-mono-tech text-xs text-muted-foreground">
                Capacidad Total: <strong className="text-foreground">{totalGallons} Galones</strong> ({cisternHeightCm} cm)
              </div>
            </div>

            {/* Controls & Telemetry */}
            <div className="lg:col-span-8 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono-tech">
                <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
                  <div className="text-[10px] text-muted-foreground uppercase">Nivel de Agua</div>
                  <div className="text-xl font-bold text-sky-500 mt-0.5">{waterLevelCm.toFixed(1)} cm</div>
                  <div className="text-[9px] text-muted-foreground">de {cisternHeightCm} cm</div>
                </div>

                <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
                  <div className="text-[10px] text-muted-foreground uppercase">Reserva Actual</div>
                  <div className="text-xl font-bold text-foreground mt-0.5">{currentGallons} gal</div>
                  <div className="text-[9px] text-emerald-500">Autonomía: ~{Math.round(currentGallons / 45)} días</div>
                </div>

                <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
                  <div className="text-[10px] text-muted-foreground uppercase">Bomba de Presión</div>
                  <div className={`text-base font-bold mt-1 flex items-center gap-1.5 ${pumpActive ? "text-emerald-500" : "text-muted-foreground"}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${pumpActive ? "bg-emerald-500 animate-ping" : "bg-muted-foreground"}`} />
                    {pumpActive ? "OPERANDO" : "APAGADA"}
                  </div>
                  <div className="text-[9px] text-muted-foreground">Relé 30A / 110V</div>
                </div>

                <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
                  <div className="text-[10px] text-muted-foreground uppercase">Servicio AAA (PR)</div>
                  <div className={`text-base font-bold mt-1 ${aaaServiceActive ? "text-emerald-500" : "text-red-500"}`}>
                    {aaaServiceActive ? "SERVICIO ACTIVO" : "SUSPENDIDO"}
                  </div>
                  <div className="text-[9px] text-muted-foreground">Presión de Entrada</div>
                </div>
              </div>

              {/* Action Switches */}
              <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3 font-mono-tech text-xs shadow-xs">
                <div className="font-bold text-foreground flex items-center justify-between">
                  <span>Controles de Automatización Hídrica</span>
                  <span className="text-[10px] text-muted-foreground">ESP32 Firmware v1.8</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      if (!pumpActive && waterPercentage <= 15 && dryRunProtection) {
                        toast.error("No se puede encender: Nivel de agua por debajo del 15% de seguridad.");
                        return;
                      }
                      setPumpActive(!pumpActive);
                    }}
                    className={`p-3 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                      pumpActive 
                        ? "bg-emerald-500/20 border-emerald-500 text-emerald-400 font-bold"
                        : "bg-card border-border text-foreground hover:bg-card/80"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Power className="w-4 h-4" />
                      <span>Forzar Bomba de Presión</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold">{pumpActive ? "ON" : "OFF"}</span>
                  </button>

                  <button
                    onClick={() => {
                      setDryRunProtection(!dryRunProtection);
                      toast.info(dryRunProtection ? "Protección contra marcha en seco desactivada" : "Protección contra marcha en seco activada");
                    }}
                    className={`p-3 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                      dryRunProtection 
                        ? "bg-sky-500/20 border-sky-500 text-sky-400 font-bold"
                        : "bg-card border-border text-muted-foreground hover:bg-card/80"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      <span>Protección Marcha en Seco</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold">{dryRunProtection ? "ACTIVO" : "INACTIVO"}</span>
                  </button>

                  <button
                    onClick={() => {
                      const next = !aaaServiceActive;
                      setAaaServiceActive(next);
                      toast(next ? "Servicio de acueductos (AAA) reportado ACTIVO" : "⚠️ Simulación de CORTE DE AGUA AAA iniciado");
                    }}
                    className={`p-3 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                      !aaaServiceActive 
                        ? "bg-red-500/20 border-red-500 text-red-400 font-bold"
                        : "bg-card border-border text-foreground hover:bg-card/80"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Simular Corte de Agua AAA</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold">{!aaaServiceActive ? "EN CORTE" : "NORMAL"}</span>
                  </button>

                  <button
                    onClick={() => {
                      setAlertTelegramEnabled(!alertTelegramEnabled);
                      toast.success(alertTelegramEnabled ? "Alertas silenciadas" : "✓ Alertas automáticas por Telegram / WiFi activadas");
                    }}
                    className={`p-3 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                      alertTelegramEnabled 
                        ? "bg-purple-500/20 border-purple-500 text-purple-400 font-bold"
                        : "bg-card border-border text-muted-foreground hover:bg-card/80"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      <span>Alertas Móvil / Telegram</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold">{alertTelegramEnabled ? "CONECTADO" : "OFF"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. GESTOR DE APAGONES LUMA & CARGAS CRÍTICAS (LiFePO4) */}
      {/* ========================================================================= */}
      {activeTab === "luma" && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Main Grid Status Banner */}
          <div className={`p-4 rounded-xl border flex flex-col sm:flex-row items-center justify-between gap-4 font-mono-tech ${
            isGridOnline 
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
              : "bg-amber-500/15 border-amber-500/50 text-amber-500"
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg border ${isGridOnline ? "bg-emerald-500/20 border-emerald-500/40" : "bg-amber-500/30 border-amber-500"}`}>
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase font-bold tracking-wider">
                  {isGridOnline ? "RED ELÉCTRICA LUMA EN LÍNEA" : "MODO ISLA: APAGÓN LUMA DETECTADO"}
                </div>
                <div className="text-[11px] opacity-80 mt-0.5">
                  {isGridOnline ? "Voltaje nominal: 120V AC · 60 Hz estable" : "Alimentando cargas esenciales con Batería LiFePO4 + Paneles Solares"}
                </div>
              </div>
            </div>

            <button
              onClick={toggleGrid}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all cursor-pointer ${
                isGridOnline
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
              }`}
            >
              {isGridOnline ? "Simular Caída de Red (Apagón)" : "Restablecer Red LUMA"}
            </button>
          </div>

          {/* Telemetry Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono-tech">
            <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
              <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1 font-semibold">
                <BatteryCharging className="w-3.5 h-3.5 text-emerald-500" /> Batería LiFePO4
              </div>
              <div className="text-2xl font-bold text-foreground mt-1">{batterySoC}%</div>
              <div className="text-[9px] text-muted-foreground mt-0.5">{(batteryCapacityWh * (batterySoC / 100)).toFixed(0)} Wh disponibles</div>
            </div>

            <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
              <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1 font-semibold">
                <Sun className="w-3.5 h-3.5 text-amber-500" /> Generación Solar
              </div>
              <div className="text-2xl font-bold text-amber-500 mt-1">{solarGenerationWatts} W</div>
              <div className="text-[9px] text-muted-foreground mt-0.5">4 Paneles 150W (PR Sol)</div>
            </div>

            <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
              <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1 font-semibold">
                <Activity className="w-3.5 h-3.5 text-sky-500" /> Consumo Activo
              </div>
              <div className="text-2xl font-bold text-sky-500 mt-1">{totalActiveWatts} W</div>
              <div className="text-[9px] text-muted-foreground mt-0.5">{criticalLoads.filter(l => l.active).length} circuitos encendidos</div>
            </div>

            <div className="bg-muted/40 border border-border p-3.5 rounded-xl shadow-xs">
              <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1 font-semibold">
                <Clock className="w-3.5 h-3.5 text-purple-500" /> Autonomía Estimada
              </div>
              <div className="text-2xl font-bold text-purple-500 mt-1">
                {remainingHours === 999 ? "∞ Ilimitada" : `${remainingHours} hrs`}
              </div>
              <div className="text-[9px] text-muted-foreground mt-0.5">
                {netPowerWatts >= 0 ? "Solar cubre consumo" : "Descargando batería"}
              </div>
            </div>
          </div>

          {/* Interactive Critical Load Manager */}
          <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-4 shadow-xs">
            <div className="flex items-center justify-between font-mono-tech">
              <div>
                <h4 className="text-sm font-bold text-foreground">Gestión Dinámica de Cargas del Hogar</h4>
                <p className="text-[11px] text-muted-foreground">Prioriza circuitos esenciales para maximizar las horas de respaldo durante emergencias.</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-lg bg-card border border-border text-foreground font-bold shadow-xs">
                Total: {totalActiveWatts} W
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono-tech">
              {criticalLoads.map((load) => (
                <div
                  key={load.id}
                  onClick={() => toggleLoad(load.id)}
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between shadow-xs ${
                    load.active
                      ? "bg-card border-primary/60"
                      : "bg-card/60 border-border opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg border ${
                      load.active ? "bg-primary/10 border-primary/30 text-primary" : "bg-card border-border text-muted-foreground"
                    }`}>
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        {load.name}
                        {load.critical && (
                          <span className="text-[8px] bg-red-500/10 text-red-400 border border-red-500/30 px-1 rounded uppercase">
                            Crítico
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Consumo: <strong className="text-foreground">{load.powerWatts} W</strong>
                      </div>
                    </div>
                  </div>

                  <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${
                    load.active ? "bg-primary" : "bg-muted-foreground/30"
                  }`}>
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      load.active ? "translate-x-4" : "translate-x-0"
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. HUERTO HIDROPÓNICO & RIEGO SOLAR */}
      {/* ========================================================================= */}
      {activeTab === "hydro" && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 font-mono-tech">
            <div className="bg-muted/40 border border-border p-4 rounded-xl space-y-2 shadow-xs">
              <div className="text-xs text-muted-foreground uppercase flex items-center gap-1.5 font-semibold">
                <Droplets className="w-4 h-4 text-sky-500" /> Humedad de Sustrato
              </div>
              <div className="text-3xl font-bold text-sky-500">{moistureLevel}%</div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden border border-border">
                <div className="bg-sky-500 h-full rounded-full" style={{ width: `${moistureLevel}%` }} />
              </div>
              <div className="text-[10px] text-emerald-500">Rango Óptimo: 60% - 75%</div>
            </div>

            <div className="bg-muted/40 border border-border p-4 rounded-xl space-y-2 shadow-xs">
              <div className="text-xs text-muted-foreground uppercase flex items-center gap-1.5 font-semibold">
                <Activity className="w-4 h-4 text-emerald-500" /> pH de la Solución
              </div>
              <div className="text-3xl font-bold text-emerald-500">{phLevel} pH</div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden border border-border">
                <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(phLevel / 14) * 100}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground">Nutrientes asimilables: 5.8 - 6.5</div>
            </div>

            <div className="bg-muted/40 border border-border p-4 rounded-xl space-y-2 shadow-xs">
              <div className="text-xs text-muted-foreground uppercase flex items-center gap-1.5 font-semibold">
                <Zap className="w-4 h-4 text-amber-500" /> Conductividad (EC)
              </div>
              <div className="text-3xl font-bold text-amber-500">{ecLevel} mS/cm</div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden border border-border">
                <div className="bg-amber-500 h-full rounded-full" style={{ width: `${(ecLevel / 3.0) * 100}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground">Concentración de sales nutritivas</div>
            </div>
          </div>

          {/* Hydroponic Pump Automation Controls */}
          <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-4 font-mono-tech shadow-xs">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-foreground">Ciclo de Riego Solar por Gravedad</h4>
                <p className="text-[11px] text-muted-foreground">Bomba sumergible 12V alimentada por micro-panel solar.</p>
              </div>
              <button
                onClick={() => {
                  setHydroPumpRunning(!hydroPumpRunning);
                  toast.success(hydroPumpRunning ? "Riego detenido" : "✓ Bombeo hidropónico activado");
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all cursor-pointer shadow-xs ${
                  hydroPumpRunning ? "bg-emerald-500 text-white" : "bg-card border border-border text-muted-foreground"
                }`}
              >
                {hydroPumpRunning ? "En Ciclo Activo" : "Pausado"}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 bg-card border border-border rounded-xl space-y-2 shadow-xs">
                <div className="text-xs font-bold text-foreground">Tiempo de Riego Activo: {pumpOnMinutes} min</div>
                <input
                  type="range"
                  min="5"
                  max="30"
                  value={pumpOnMinutes}
                  onChange={(e) => setPumpOnMinutes(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="text-[10px] text-muted-foreground">Oxigena raíces e inyecta solución mineral a las torres verticales.</div>
              </div>

              <div className="p-3.5 bg-card border border-border rounded-xl space-y-2 shadow-xs">
                <div className="text-xs font-bold text-foreground">Intervalo de Drenaje: {pumpOffMinutes} min</div>
                <input
                  type="range"
                  min="15"
                  max="90"
                  step="5"
                  value={pumpOffMinutes}
                  onChange={(e) => setPumpOffMinutes(Number(e.target.value))}
                  className="w-full accent-sky-500 cursor-pointer"
                />
                <div className="text-[10px] text-muted-foreground">Drenaje por gravedad evitando pudrición radicular en clima cálido de PR.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. HARDWARE REAL & GUÍA DE ABASTO EN PUERTO RICO (ESP32 IoT) */}
      {/* ========================================================================= */}
      {activeTab === "hardware" && (
        <div className="space-y-6 animate-in fade-in duration-300 font-mono-tech">
          
          {/* Live ESP32 Telemetry Connection Box */}
          <div className="bg-card border border-purple-500/40 rounded-xl p-5 space-y-4 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-purple-400" />
                  <h4 className="text-sm font-bold text-foreground uppercase tracking-wide">
                    Telemetría en Vivo desde tu Microcontrolador ESP32
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Conecta la IP de tu placa en la red Wi-Fi de tu casa para alimentar los paneles de Cisterna y LUMA con mediciones físicas reales.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={downloadFirmwareCode}
                  className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-md cursor-pointer whitespace-nowrap"
                >
                  <Cpu className="w-3.5 h-3.5" />
                  <span>📥 Descargar Firmware .INO</span>
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2 border-t border-border">
              <div className="flex-1 w-full flex items-center gap-2 bg-muted/40 border border-border rounded-xl px-3 py-2 text-xs shadow-xs">
                <Radio className="w-4 h-4 text-purple-400 shrink-0" />
                <span className="text-muted-foreground font-semibold">IP Local ESP32:</span>
                <input
                  type="text"
                  value={esp32Ip}
                  onChange={(e) => setEsp32Ip(e.target.value)}
                  placeholder="ej. 192.168.1.185"
                  className="bg-transparent border-none outline-none text-foreground font-bold flex-1"
                />
              </div>

              <button
                type="button"
                onClick={syncLiveESP32}
                disabled={isSyncing}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-all shadow-xs"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? "animate-spin" : ""}`} />
                <span>{isSyncing ? "Conectando..." : "Sincronizar Datos Reales"}</span>
              </button>
            </div>

            {lastSyncTime && (
              <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                <span>Última lectura física recibida: {lastSyncTime}</span>
              </div>
            )}
          </div>

          {/* Real Sensors BOM for Puerto Rico */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4 shadow-xs">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <h4 className="text-sm font-bold text-foreground">
                  🛒 Lista de Sensores & Piezas Reales para Construir en tu Casa (Puerto Rico)
                </h4>
                <p className="text-[11px] text-muted-foreground">
                  Componentes seleccionados específicamente para resistir alta humedad tropical, salitre y cortes de energía.
                </p>
              </div>
              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-bold">
                Presupuesto Aprox: ~$88 USD
              </span>
            </div>

            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-border text-muted-foreground uppercase text-[10px]">
                    <th className="py-2 px-2">Sub-Sistema</th>
                    <th className="py-2 px-2">Sensor / Componente Real</th>
                    <th className="py-2 px-2">¿Por qué este modelo en PR?</th>
                    <th className="py-2 px-2">Tienda / Proveedor</th>
                    <th className="py-2 px-2 text-right">Precio Estimado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-sky-400">💧 Cisterna</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Sensor Ultrasónico Impermeable JSN-SR04T v3.0
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Sonda sellada IP67 con cable de 2.5m. No se oxida ni sufre por condensación dentro del tanque.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">Mouser / Amazon (USPS)</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$14.50</td>
                  </tr>

                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-purple-400">🧠 Cerebro IoT</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Placa Microcontrolador ESP32-S3 Dual-Core (Wi-Fi/BLE)
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Procesador dual a 240MHz. Soporta antena externa IPEX para atravesar paredes de hormigón en PR.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">Mouser / Digi-Key (USPS)</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$9.80</td>
                  </tr>

                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-amber-400">⚡ Monitor LUMA</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Transformador de Voltaje ZMPT101B (Aislamiento Galvánico)
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Lee si la red está en 120V o si cayó por apagón. Aislamiento de 4000V para no quemar el micro.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">Amazon / Mouser (USPS)</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$6.20</td>
                  </tr>

                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-amber-400">⚡ Consumo Casa</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Sensor Toroidal no Invasivo SCT-013-000 (100A/50mA)
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Se abraza al cable vivo principal en la caja de breakers sin cortar ni pelar cables activos.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">Amazon / Mouser (USPS)</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$11.00</td>
                  </tr>

                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-emerald-400">🛡️ Potencia Bomba</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Contactor Bipolar AC 120V 25A (Bobina 110V)
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Enciende y apaga la bomba de 1/2 HP o 3/4 HP de forma segura sin peligro de chispazos ni arcos.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">Home Depot PR / National Lumber</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$18.99</td>
                  </tr>

                  <tr className="hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-bold text-muted-foreground">📦 Cerramiento</td>
                    <td className="py-2.5 px-2 font-bold text-foreground">
                      Gabinete Estanco NEMA 4X / IP65 con Prensacables
                    </td>
                    <td className="py-2.5 px-2 text-[10.5px] text-muted-foreground">
                      Protege la electrónica contra lluvia torrencial tropical, lagartijos y salitre costero.
                    </td>
                    <td className="py-2.5 px-2 text-primary font-bold">National Lumber / Ferreterías Ace</td>
                    <td className="py-2.5 px-2 text-right font-bold text-foreground">$16.50</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="p-3.5 bg-muted/40 border border-border rounded-xl text-[11px] text-muted-foreground leading-relaxed shadow-xs">
              💡 <strong className="text-foreground">Consejo de Envío a Puerto Rico:</strong> Para microcontroladores, transductores JSN-SR04T y módulos ZMPT101B, la vía más económica y rápida es pedir por <strong>Mouser Electronics</strong> o <strong>Amazon</strong> con envío doméstico estándar vía el <strong>Servicio Postal de EE. UU. (USPS)</strong>; tarda habitualmente de 3 a 5 días laborables sin tarifas internacionales adicionales.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
